import { useState, useRef, useEffect } from "react";
import {
  Camera,
  Upload,
  Store,
  Calendar,
  ChevronDown,
  X,
  AlertCircle,
  Loader2,
  ShoppingBag,
  CheckSquare,
  ArrowLeft,
  Zap,
  Edit3,
  FileText,
  Send,
  CreditCard,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";

const CATEGORIES = [
  "Groceries",
  "Dining",
  "Transport",
  "Home & Garden",
  "Health",
  "Shopping",
  "Entertainment",
  "Other",
];

const getCurrencySymbol = (code) => {
  if (!code) return "£";
  const map = { GBP: "£", USD: "$", EUR: "€", INR: "₹" };
  return map[code.toUpperCase()] || "£";
};

// Claude returns category names directly — validate against our list, fallback to "Other"
const normaliseCategory = (raw) => {
  if (!raw) return "Other";
  const match = CATEGORIES.find((c) => c.toLowerCase() === String(raw).toLowerCase().trim());
  return match || "Other";
};

// ---------- Buddy error illustration (embedded, no external dependency) ----------
const BUDDY_ERROR_IMG = "data:image/webp;base64,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";

// ---------- Buddy camera illustration (for scan screen) ----------
const BUDDY_CAMERA_IMG = "data:image/webp;base64,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";

// ---------- Buddy error popup ----------
const BuddyErrorPopup = ({ errorType, isUploading, onSaveAnyway, onTryAgain, onEnterManually, onClose }) => {
  if (!errorType) return null;
  const isNotReceipt = errorType === "not_receipt";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-5"
      style={{ backgroundColor: "rgba(15,23,42,0.6)", backdropFilter: "blur(3px)" }}
      data-testid="buddy-error-popup"
    >
      <div
        className="bg-white relative flex flex-col items-center text-center"
        style={{
          width: "100%",
          maxWidth: 400,
          borderRadius: 24,
          padding: "32px 24px 28px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
          animation: "buddyPopIn 0.25s cubic-bezier(0.34,1.56,0.64,1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute flex items-center justify-center"
          style={{ top: 14, right: 14, width: 32, height: 32, borderRadius: 999, backgroundColor: "#F3F4F6", color: "#6B7280", border: "none", cursor: "pointer" }}
          aria-label="Close"
          data-testid="buddy-error-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div style={{ marginBottom: 18 }}>
          <img
            src={BUDDY_ERROR_IMG}
            alt="Buddy is having trouble"
            style={{ width: 140, height: 140, objectFit: "contain" }}
          />
        </div>
        <h2 style={{ fontFamily: "Manrope, sans-serif", fontWeight: 800, fontSize: 20, color: "#111827", marginBottom: 10, lineHeight: 1.3, whiteSpace: "pre-line" }}>
          {isNotReceipt
            ? "This doesn't seem\nlike a receipt."
            : <><span>Buddy's having a </span><span style={{ color: "#2D5BFF" }}>hard time</span><span> making sense of this receipt.</span></>
          }
        </h2>
        <p style={{ fontFamily: "DM Sans, sans-serif", fontSize: 14, color: "#6B7280", marginBottom: 24, lineHeight: 1.6 }}>
          {isNotReceipt
            ? "Please capture a photo of the receipt. We'll be able to read it better."
            : "No worries! You can still save it and I'll take another look using my smarts later."
          }
        </p>
        <div className="w-full flex flex-col gap-3">
          {isNotReceipt ? (
            <button
              onClick={onTryAgain}
              className="w-full flex items-center justify-center gap-2"
              style={{ height: 52, borderRadius: 16, background: "linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)", color: "#FFFFFF", fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", border: "none", cursor: "pointer", boxShadow: "0 8px 20px rgba(45,91,255,0.25)" }}
              data-testid="recapture-btn"
            >
              <Camera className="w-4 h-4" />
              Recapture image
            </button>
          ) : (
            <>
              <button
                onClick={onSaveAnyway}
                disabled={isUploading}
                className="w-full flex items-center gap-4"
                style={{ height: 58, borderRadius: 16, background: isUploading ? "#93C5FD" : "linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)", color: "#FFFFFF", fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", border: "none", cursor: isUploading ? "not-allowed" : "pointer", padding: "0 20px", boxShadow: "0 8px 20px rgba(45,91,255,0.25)" }}
                data-testid="popup-save-anyway-btn"
              >
                <Upload className="w-4 h-4 flex-shrink-0" />
                <div style={{ textAlign: "left" }}>
                  <div>{isUploading ? "Saving…" : "Save anyway"}</div>
                  <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 1 }}>I'll process it in the background</div>
                </div>
              </button>
              <button
                onClick={onTryAgain}
                className="w-full flex items-center gap-4"
                style={{ height: 58, borderRadius: 16, backgroundColor: "#FFFFFF", border: "1.5px solid #E5E7EB", color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "DM Sans, sans-serif", cursor: "pointer", padding: "0 20px" }}
                data-testid="popup-try-again-btn"
              >
                <Camera className="w-4 h-4 flex-shrink-0" style={{ color: "#6B7280" }} />
                <div style={{ textAlign: "left" }}>
                  <div>Try another photo</div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#9CA3AF", marginTop: 1 }}>Maybe a different angle will help</div>
                </div>
              </button>
              <button
                onClick={onEnterManually}
                className="w-full flex items-center gap-4"
                style={{ height: 58, borderRadius: 16, backgroundColor: "#FFFFFF", border: "1.5px solid #E5E7EB", color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "DM Sans, sans-serif", cursor: "pointer", padding: "0 20px" }}
                data-testid="popup-enter-manually-btn"
              >
                <Edit3 className="w-4 h-4 flex-shrink-0" style={{ color: "#6B7280" }} />
                <div style={{ textAlign: "left" }}>
                  <div>Enter details manually</div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#9CA3AF", marginTop: 1 }}>Add the information yourself</div>
                </div>
              </button>
            </>
          )}
        </div>
        <style>{`
          @keyframes buddyPopIn {
            from { opacity: 0; transform: scale(0.88) translateY(12px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
};

// ---------- Receipt cropper ----------
const CropperOverlay = ({ imageSrc, onCrop, onSkip, isMobile }) => {
  const imgRef  = useRef(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const [crop, setCrop] = useState({ x: 5, y: 5, w: 90, h: 90 });

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const MIN = 15;

  const getPos = (e) => {
    const wrap = wrapRef.current;
    if (!wrap) return { x: 0, y: 0 };
    const rect = wrap.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return { x: ((cx - rect.left) / rect.width) * 100, y: ((cy - rect.top) / rect.height) * 100 };
  };

  const startDrag = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const p = getPos(e);
    dragRef.current = { type, startX: p.x, startY: p.y, startCrop: { ...crop } };

    const onMove = (ev) => {
      ev.preventDefault();
      if (!dragRef.current) return;
      const { startX, startY, startCrop: sc, type: t } = dragRef.current;
      const cur = getPos(ev);
      const dx = cur.x - startX, dy = cur.y - startY;
      setCrop(() => {
        let { x, y, w, h } = sc;
        if (t === 'move') { x = clamp(sc.x + dx, 0, 100 - sc.w); y = clamp(sc.y + dy, 0, 100 - sc.h); }
        else if (t === 'nw') { x = clamp(sc.x + dx, 0, sc.x + sc.w - MIN); y = clamp(sc.y + dy, 0, sc.y + sc.h - MIN); w = sc.w + (sc.x - x); h = sc.h + (sc.y - y); }
        else if (t === 'ne') { y = clamp(sc.y + dy, 0, sc.y + sc.h - MIN); h = sc.h + (sc.y - y); w = clamp(sc.w + dx, MIN, 100 - sc.x); }
        else if (t === 'sw') { x = clamp(sc.x + dx, 0, sc.x + sc.w - MIN); w = sc.w + (sc.x - x); h = clamp(sc.h + dy, MIN, 100 - sc.y); }
        else if (t === 'se') { w = clamp(sc.w + dx, MIN, 100 - sc.x); h = clamp(sc.h + dy, MIN, 100 - sc.y); }
        return { x, y, w, h };
      });
    };
    const onEnd = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
    document.addEventListener('mousemove', onMove, { passive: false });
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img) { onSkip(); return; }
    const cx = Math.round((crop.x / 100) * img.naturalWidth);
    const cy = Math.round((crop.y / 100) * img.naturalHeight);
    const cw = Math.round((crop.w / 100) * img.naturalWidth);
    const ch = Math.round((crop.h / 100) * img.naturalHeight);
    if (cw < 50 || ch < 50) { onSkip(); return; }
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    canvas.getContext('2d').drawImage(img, cx, cy, cw, ch, 0, 0, cw, ch);
    canvas.toBlob((blob) => {
      if (!blob) { onSkip(); return; }
      onCrop(new File([blob], 'cropped-receipt.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  const IMG_MAX_H = isMobile ? 'calc(100vh - 230px)' : '58vh';
  const corners = [
    { key: 'nw', cx: crop.x,          cy: crop.y },
    { key: 'ne', cx: crop.x + crop.w, cy: crop.y },
    { key: 'sw', cx: crop.x,          cy: crop.y + crop.h },
    { key: 'se', cx: crop.x + crop.w, cy: crop.y + crop.h },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#0B1120' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px 8px', flexShrink: 0 }}>
        <button onClick={onSkip} style={{ color: '#94A3B8', fontSize: 14, fontFamily: 'DM Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 40 }}>Skip</button>
        <p style={{ flex: 1, textAlign: 'center', color: '#FFFFFF', fontSize: 17, fontWeight: 700, fontFamily: 'Manrope, sans-serif', margin: 0 }}>Crop receipt</p>
        <button onClick={handleConfirm} style={{ color: '#60A5FA', fontSize: 14, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 40, textAlign: 'right' }}>Done</button>
      </div>
      <p style={{ textAlign: 'center', color: '#475569', fontSize: 12, fontFamily: 'DM Sans, sans-serif', margin: '0 0 10px', flexShrink: 0 }}>Drag corners to frame the receipt</p>

      <div className="flex-1 flex items-center justify-center overflow-hidden" style={{ padding: '0 12px' }}>
        <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', maxHeight: IMG_MAX_H, lineHeight: 0, touchAction: 'none', userSelect: 'none' }}>
          <img ref={imgRef} src={imageSrc} alt="Crop" draggable={false}
            style={{ display: 'block', maxWidth: '100%', maxHeight: IMG_MAX_H, objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }} />

          {/* Dark overlay — 4 rects outside crop */}
          <div style={{ position: 'absolute', left: 0, top: 0, right: 0, height: `${crop.y}%`, background: 'rgba(0,0,0,0.62)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', left: 0, bottom: 0, right: 0, height: `${100 - crop.y - crop.h}%`, background: 'rgba(0,0,0,0.62)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', left: 0, top: `${crop.y}%`, width: `${crop.x}%`, height: `${crop.h}%`, background: 'rgba(0,0,0,0.62)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', right: 0, top: `${crop.y}%`, width: `${100 - crop.x - crop.w}%`, height: `${crop.h}%`, background: 'rgba(0,0,0,0.62)', pointerEvents: 'none' }} />

          {/* Crop border + drag-to-move interior */}
          <div
            style={{ position: 'absolute', left: `${crop.x}%`, top: `${crop.y}%`, width: `${crop.w}%`, height: `${crop.h}%`, border: '1.5px solid rgba(255,255,255,0.85)', cursor: 'move', touchAction: 'none', boxSizing: 'border-box' }}
            onMouseDown={(e) => startDrag(e, 'move')} onTouchStart={(e) => startDrag(e, 'move')}
          >
            {/* Thirds grid */}
            {['33%', '67%'].map(p => <div key={p} style={{ position: 'absolute', left: p, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.15)', pointerEvents: 'none' }} />)}
            {['33%', '67%'].map(p => <div key={p} style={{ position: 'absolute', top: p, left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.15)', pointerEvents: 'none' }} />)}
          </div>

          {/* Corner handles */}
          {corners.map(({ key, cx, cy }) => (
            <div key={key}
              style={{ position: 'absolute', left: `calc(${cx}% - 16px)`, top: `calc(${cy}% - 16px)`, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20, touchAction: 'none', cursor: `${key}-resize` }}
              onMouseDown={(e) => startDrag(e, key)} onTouchStart={(e) => startDrag(e, key)}
            >
              <div style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: '#FFFFFF', boxShadow: '0 2px 6px rgba(0,0,0,0.45)' }} />
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: isMobile ? '14px 20px 36px' : '14px 20px 20px', flexShrink: 0 }}>
        <button onClick={handleConfirm}
          style={{ width: '100%', height: 52, borderRadius: 16, background: 'linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)', color: '#FFFFFF', fontSize: 16, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', border: 'none', cursor: 'pointer', boxShadow: '0 8px 20px rgba(45,91,255,0.3)', marginBottom: 10 }}>
          ✂︎ Crop &amp; Scan
        </button>
        <button onClick={onSkip}
          style={{ width: '100%', background: 'none', border: 'none', color: '#475569', fontSize: 14, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', padding: '6px 0' }}>
          Use full image instead →
        </button>
      </div>
    </div>
  );
};

// ---------- Field row components ----------
const FieldLabel = ({ children, optional }) => (
  <label
    className="block mb-2"
    style={{ color: "#111827", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}
  >
    {children}
    {optional ? (
      <span style={{ color: "#9CA3AF", fontWeight: 500, marginLeft: 6 }}>(optional)</span>
    ) : null}
  </label>
);

const ScanInput = ({ icon: Icon, error, ...props }) => (
  <div className="relative">
    {Icon ? (
      <Icon
        className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4"
        style={{ color: error ? "#DC2626" : "#9CA3AF" }}
      />
    ) : null}
    <input
      {...props}
      className={`w-full rounded-xl ${Icon ? "pl-10" : "pl-4"} pr-3`}
      style={{
        height: 48,
        backgroundColor: "#FFFFFF",
        border: `1px solid ${error ? "#DC2626" : "#E5E7EB"}`,
        fontSize: 16,
        fontFamily: "DM Sans, sans-serif",
        color: "#111827",
        outline: "none",
      }}
    />
  </div>
);

// ---------- Main content ----------
export default function ScanReceiptContent({ onComplete, onSaved, isMobile = false }) {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'processing_error'
  const [documentType, setDocumentType] = useState(null); // 'retail_receipt' | 'bank_transfer' | 'online_order' | 'bank_statement' | 'unreadable'
  const [currencyCode, setCurrencyCode] = useState("");
  const [ocrRawResponse, setOcrRawResponse] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    merchantName: "",
    transactionDate: "",
    totalAmount: "",
    category: "",
    notes: "",
  });
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  // ── Hurry mode (persists within session) ───────────────────────────────────
  const [hurryMode, setHurryMode] = useState(() => {
    try { return sessionStorage.getItem("mybuddy_hurry_mode") === "true"; }
    catch { return false; }
  });
  const [isLongReceipt, setIsLongReceipt] = useState(false);
  const [isUploadingPending, setIsUploadingPending] = useState(false);
  const [cropperSrc, setCropperSrc] = useState(null); // shows cropper when set

  const pendingRawFileRef = useRef(null); // holds file while cropper is open

  // ── Group sharing state ───────────────────────────────────────────────────
  const [shareGroups, setShareGroups] = useState([]); // available groups
  const [selectedShareGroup, setSelectedShareGroup] = useState(null); // group to share with
  const [showGroupPicker, setShowGroupPicker] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API}/groups`, { withCredentials: true });
        setShareGroups(res.data.groups || []);
      } catch { /* silent */ }
    };
    fetchGroups();
  }, []);

  const GROUP_TYPE_CONFIG = {
    household: { icon: '🏠', bg: '#16A34A' },
    trip: { icon: '✈️', bg: '#7C3AED' },
    event: { icon: '📅', bg: '#2563EB' },
    community: { icon: '👥', bg: '#0D9488' },
    custom: { icon: '➕', bg: '#6B7280' },
  };

  const toggleHurryMode = () => {
    const next = !hurryMode;
    setHurryMode(next);
    try { sessionStorage.setItem("mybuddy_hurry_mode", String(next)); } catch { }
  };

  // Upload to /receipts/pending — used by hurry mode AND save-anyway fallback
  const uploadAsPending = async (file) => {
    setIsUploadingPending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axios.post(`${API}/receipts/pending`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
        timeout: 60000,
      });
      if (resp.data.success) {
        toast.success("Saved! We'll process it shortly.");
        onSaved?.();
        onComplete?.();
        navigate("/dashboard", { replace: true });
      } else {
        toast.error("Couldn't save. Please try again.");
      }
    } catch (err) {
      console.error("Pending upload error:", err);
      if (err.response?.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      toast.error("Couldn't save. Please try again.");
    } finally {
      setIsUploadingPending(false);
    }
  };

  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const captureSourceRef = useRef(null); // 'camera' | 'upload' — set before each click

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
    };
    if (showCategoryDropdown) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCategoryDropdown]);

  const symbol = getCurrencySymbol(currencyCode);

  // ── Core: set preview + run OCR ──────────────────────────────────────────
  const processFile = async (file) => {
    setImageFile(file);
    setError(null);
    setErrorType(null);
    setIsLongReceipt(false);

    const isHeic = file.type === "image/heic" || file.type === "image/heif"
      || file.name?.toLowerCase().endsWith(".heic")
      || file.name?.toLowerCase().endsWith(".heif");

    if (isHeic) {
      setImagePreview("heic-placeholder");
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        try {
          const img = new Image();
          img.onload = () => { if (img.naturalHeight > img.naturalWidth * 3) setIsLongReceipt(true); };
          img.src = reader.result;
        } catch (e) { /* ignore */ }
      };
      reader.readAsDataURL(file);
    }

    setIsProcessing(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("file", file);
      const response = await axios.post(`${API}/processReceiptOCR`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
        timeout: 180000,
      });
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setCurrencyCode(data.currency || "GBP");
        setOcrRawResponse(data);
        setDocumentType(data.document_type || null);
        setFormData((prev) => ({
          ...prev,
          merchantName: data.merchant || prev.merchantName,
          transactionDate: data.date || prev.transactionDate,
          totalAmount: data.total != null ? Number(data.total).toFixed(2) : prev.totalAmount,
          category: normaliseCategory(data.category) || prev.category,
          notes: data.notes_prefill || prev.notes,
        }));
        if (!data.total) {
          setError("Some details couldn't be read — please check and fill in the missing fields.");
        }
      } else if (response.data.status === "not_a_receipt") {
        setDocumentType("unreadable");
      } else {
        setErrorType("processing_error");
      }
    } catch (err) {
      console.error("OCR error:", err);
      setErrorType("processing_error");
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Image selected: show cropper for all non-HEIC images ─────────────────
  const handleImageCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Hurry mode: skip cropper + OCR entirely
    if (hurryMode) {
      await uploadAsPending(file);
      return;
    }

    const isHeic = file.type === "image/heic" || file.type === "image/heif"
      || file.name?.toLowerCase().endsWith(".heic")
      || file.name?.toLowerCase().endsWith(".heif");

    const isPDF = file.type === "application/pdf"
      || file.name?.toLowerCase().endsWith(".pdf");

    // HEIC can't be previewed in the browser — skip cropper
    if (isHeic) {
      await processFile(file);
      return;
    }

    // PDF can't be rendered in the cropper — skip cropper
    if (isPDF) {
      await processFile(file);
      return;
    }

    // All other images (camera + upload): show cropper
    pendingRawFileRef.current = file;
    const reader = new FileReader();
    reader.onloadend = () => setCropperSrc(reader.result);
    reader.readAsDataURL(file);
  };

  // ── Crop confirmed ────────────────────────────────────────────────────────
  const handleCropConfirm = async (croppedFile) => {
    setCropperSrc(null);
    pendingRawFileRef.current = null;
    await processFile(croppedFile);
  };

  // ── Crop skipped: use original file ──────────────────────────────────────
  const handleCropSkip = async () => {
    const file = pendingRawFileRef.current;
    setCropperSrc(null);
    pendingRawFileRef.current = null;
    if (file) await processFile(file);
  };

  const handleReset = () => {
    setImagePreview(null);
    setImageFile(null);
    setIsProcessing(false);
    setIsLongReceipt(false);
    setIsSaving(false);
    setError(null);
    setErrorType(null);
    setCropperSrc(null);
    pendingRawFileRef.current = null;
    setCurrencyCode("");
    setOcrRawResponse(null);
    setDocumentType(null);
    setValidationErrors({});
    setFormData({ merchantName: "", transactionDate: "", totalAmount: "", category: "", notes: "" });
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.merchantName?.trim()) errors.merchantName = "Enter merchant name";
    if (!formData.transactionDate) errors.transactionDate = "Select a date";
    const amount = parseFloat(formData.totalAmount);
    if (!formData.totalAmount || isNaN(amount) || amount <= 0) errors.totalAmount = "Enter valid amount";
    if (!formData.category) errors.category = "Select a category";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleComplete = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const receiptData = {
        merchant: formData.merchantName.trim(),
        amount: parseFloat(formData.totalAmount),
        currency: currencyCode || "GBP",
        transaction_date: formData.transactionDate || null,
        category: formData.category || null,
        notes: formData.notes || null,
        receipt_image_url: imagePreview && imagePreview !== "heic-placeholder"
          ? imagePreview
          : null,
        // Rich Claude fields — stored for receipt detail page + insights
        subtotal: ocrRawResponse?.subtotal ?? null,
        total_discount: ocrRawResponse?.total_discount ?? null,
        payment_method: ocrRawResponse?.payment_method ?? null,
        card_type: ocrRawResponse?.card_type ?? null,
        card_last4: ocrRawResponse?.card_last4 ?? null,
        payment_type: ocrRawResponse?.payment_type ?? null,
        items: ocrRawResponse?.items ?? [],
        discounts: ocrRawResponse?.discounts ?? [],
        loyalty_points_earned: ocrRawResponse?.loyalty_points_earned ?? null,
        loyalty_points_balance: ocrRawResponse?.loyalty_points_balance ?? null,
        receipt_number: ocrRawResponse?.receipt_number ?? null,
        customer_name: ocrRawResponse?.customer_name ?? null,
        claude_raw_response: ocrRawResponse ?? null,
      };
      const response = await axios.post(`${API}/receipts`, receiptData, { withCredentials: true });
      if (response.data.success) {
        // Share to group if one is selected
        if (selectedShareGroup && response.data.receipt?.id) {
          try {
            await axios.post(`${API}/groups/${selectedShareGroup.id}/share`, {
              item_id: response.data.receipt.id,
              item_type: "receipt",
            }, { withCredentials: true });
            toast.success(`Receipt saved & shared with ${selectedShareGroup.name}`);
          } catch {
            toast.success("Receipt saved (couldn't share to circle)");
          }
        } else {
          toast.success("Receipt saved");
        }
        onSaved?.();
        onComplete?.();
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      console.error("Save error:", err);
      if (err.response?.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      if (err.response?.data?.detail?.validation_errors) {
        setValidationErrors(err.response.data.detail.validation_errors);
      } else {
        setError("Failed to save receipt. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ---------- Layout: 1-col on mobile, 2-col on web ----------
  return (
    <div className={isMobile ? "flex flex-col" : "grid grid-cols-2 gap-6"}>

      {/* Scan line keyframe */}
      <style>{`
        @keyframes scanDown {
          0%   { top: 0%;   opacity: 1; }
          80%  { top: 100%; opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes progressPulse {
          0%   { width: 35%; }
          100% { width: 82%; }
        }
        .scan-line-anim {
          position: absolute; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent, #2D5BFF 40%, #2D5BFF 60%, transparent);
          animation: scanDown 1.8s ease-in-out infinite;
          pointer-events: none;
        }
        .progress-pulse {
          animation: progressPulse 2s ease-in-out infinite alternate;
        }
      `}</style>

      {/* LEFT: Upload zone */}
      <div className={isMobile ? "" : "flex flex-col gap-4"}>
        <div
          className="relative flex flex-col items-center justify-center text-center"
          style={{
            border: imagePreview ? "none" : "2px dashed #BFDBFE",
            backgroundColor: imagePreview ? "#F1F5F9" : "#EFF6FF",
            borderRadius: 18,
            padding: isMobile ? "28px 20px" : "36px 24px",
            minHeight: isMobile ? (imagePreview ? "auto" : 220) : 360,
          }}
          data-testid="upload-zone"
        >
          {imagePreview ? (
            <div className="relative w-full overflow-hidden rounded-xl">
              {imagePreview === "heic-placeholder" ? (
                <div
                  className="w-full flex flex-col items-center justify-center gap-2 relative"
                  style={{ height: isMobile ? 160 : 240, backgroundColor: "#1a1a2e" }}
                >
                  {/* animated placeholder lines */}
                  <div style={{ width: "70%", display: "flex", flexDirection: "column", gap: 8, opacity: 0.35 }}>
                    {[70, 90, 80, 60, 85].map((w, i) => (
                      <div key={i} style={{ height: 6, borderRadius: 3, background: "#fff", width: `${w}%` }} />
                    ))}
                  </div>
                  {isProcessing && <div className="scan-line-anim" />}
                  {!isProcessing && (
                    <p style={{ position: "absolute", bottom: 10, color: "rgba(255,255,255,0.6)", fontSize: 11, fontFamily: "DM Sans, sans-serif" }}>
                      HEIC — preview not available
                    </p>
                  )}
                </div>
              ) : imagePreview?.startsWith("data:application/pdf") ? (
                <div
                  className="w-full flex flex-col items-center justify-center gap-3 relative"
                  style={{ height: isMobile ? 160 : 240, backgroundColor: "#1a1a2e" }}
                >
                  <div style={{ width: 56, height: 56, borderRadius: 14, backgroundColor: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FileText className="w-7 h-7" style={{ color: "rgba(255,255,255,0.7)" }} />
                  </div>
                  {isProcessing && <div className="scan-line-anim" />}
                  {!isProcessing && (
                    <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, fontFamily: "DM Sans, sans-serif", fontWeight: 600 }}>
                      PDF uploaded
                    </p>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Receipt preview"
                    className="w-full object-contain rounded-xl"
                    style={{ maxHeight: isMobile ? 220 : 320, imageOrientation: "from-image" }}
                  />
                  {isProcessing && <div className="scan-line-anim" style={{ borderRadius: 12 }} />}
                </div>
              )}
              {/* Remove button — only when not processing */}
              {!isProcessing && (
                <button
                  onClick={handleReset}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#FFFFFF", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                  data-testid="remove-image-btn"
                >
                  <X className="w-4 h-4" style={{ color: "#71717A" }} />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center mb-4" style={{ width: 64, height: 64, borderRadius: 999, backgroundColor: "#DBEAFE" }}>
                <Camera className="w-7 h-7" style={{ color: PRIMARY, strokeWidth: 2 }} />
              </div>
              <p className="mb-1" style={{ color: "#111827", fontSize: 16, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                Upload receipt
              </p>
              <p className="mb-5" style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.6, textAlign: "center" }}>
                Take a photo or upload<br />from your gallery
              </p>
              <div className="flex items-center gap-3 w-full">
                <button
                  onClick={() => { captureSourceRef.current = "camera"; cameraInputRef.current?.click(); }}
                  className="flex-1 flex items-center justify-center gap-2 rounded-2xl"
                  style={{ height: 48, backgroundColor: "#FFFFFF", border: "1.5px solid #E5E7EB", color: "#111827", fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}
                  data-testid="take-photo-btn"
                >
                  <Camera className="w-4 h-4" style={{ color: PRIMARY }} />
                  Camera
                </button>
                <button
                  onClick={() => { captureSourceRef.current = "upload"; uploadInputRef.current?.click(); }}
                  className="flex-1 flex items-center justify-center gap-2 rounded-2xl"
                  style={{ height: 48, backgroundColor: "#FFFFFF", border: "1.5px solid #E5E7EB", color: "#111827", fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}
                  data-testid="choose-file-btn"
                >
                  <Upload className="w-4 h-4" style={{ color: PRIMARY }} />
                  Upload
                </button>
              </div>
            </>
          )}
        </div>

        {/* RECOMMENDED — Quick Save (only show before image is selected) */}
        {!imagePreview && !isProcessing && (
          <>
            {/* Divider */}
            <div className="flex items-center gap-3" style={{ margin: "4px 0 -2px" }}>
              <div className="flex-1 h-px" style={{ backgroundColor: "#E5E7EB" }} />
              <span style={{ color: "#9CA3AF", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", fontFamily: "DM Sans, sans-serif" }}>RECOMMENDED</span>
              <div className="flex-1 h-px" style={{ backgroundColor: "#E5E7EB" }} />
            </div>

            {/* Quick Save card with Buddy mascot */}
            <div
              style={{
                background: hurryMode ? "linear-gradient(135deg, #EEF2FF, #DBEAFE)" : "#EEF3FF",
                borderRadius: 20,
                padding: "18px 16px",
                border: hurryMode ? `2px solid ${PRIMARY}` : "1px solid #BFDBFE",
              }}
            >
              <button
                type="button"
                onClick={toggleHurryMode}
                className="w-full flex items-center gap-3 text-left"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                data-testid="hurry-mode-toggle"
              >
                {/* Buddy mascot */}
                <div style={{ position: "relative", flexShrink: 0, width: 56, height: 56 }}>
                  <img
                    src={BUDDY_CAMERA_IMG}
                    alt="Buddy"
                    style={{ width: 56, height: 56, objectFit: "contain" }}
                  />
                  <div style={{ position: "absolute", top: -4, right: -4, width: 20, height: 20, borderRadius: 999, backgroundColor: PRIMARY, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Zap className="w-2.5 h-2.5" style={{ color: "#fff" }} />
                  </div>
                </div>
                {/* Text */}
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 15, fontWeight: 700, fontFamily: "Manrope, sans-serif", color: "#111827", marginBottom: 2 }}>
                    Quick Save
                  </p>
                  <p style={{ fontSize: 12, fontFamily: "DM Sans, sans-serif", color: "#6B7280", lineHeight: 1.4 }}>
                    Snap and go — Buddy will<br />take care of the rest.
                  </p>
                </div>
                {/* Toggle */}
                <div
                  style={{
                    width: 48, height: 28, borderRadius: 14,
                    backgroundColor: hurryMode ? PRIMARY : "#D1D5DB",
                    position: "relative", transition: "background-color 0.2s ease", flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 24, height: 24, borderRadius: 12, backgroundColor: "#FFFFFF",
                      position: "absolute", top: 2, left: hurryMode ? 22 : 2,
                      transition: "left 0.2s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                    }}
                  />
                </div>
              </button>
              <p style={{ textAlign: "center", color: "#9CA3AF", fontSize: 11, fontFamily: "DM Sans, sans-serif", marginTop: 12, lineHeight: 1.4 }}>
                You can review &amp; edit later anytime.
              </p>
            </div>
          </>
        )}

        {/* Tip banner — hide while processing and for non-receipt document types */}
        {!isProcessing && (!documentType || documentType === "retail_receipt") && (
          <div className="flex items-start gap-3 rounded-xl" style={{ backgroundColor: "#FEF3C7", border: "1px solid #FDE68A", padding: "12px 14px" }}>
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#D97706" }} />
            <p style={{ color: "#78350F", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
              For long receipts, lay flat and photograph from directly above for best results.
            </p>
          </div>
        )}

        {/* SAVE TO section */}
        {!isProcessing && (
          <div>
            <p style={{ color: "#6B7280", fontSize: 11, fontWeight: 700, fontFamily: "DM Sans, sans-serif", letterSpacing: 1, marginBottom: 8 }}>SAVE TO</p>
            <div style={{ backgroundColor: "#FFFFFF", borderRadius: 16, border: "1px solid #F3F4F6", padding: "10px 14px" }}>
              {/* Personal — always shown */}
              <div className="flex items-center gap-3 py-2">
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: PRIMARY, color: "#FFFFFF", fontWeight: 700, fontSize: 12, fontFamily: "Manrope, sans-serif" }}>
                  R
                </div>
                <div className="flex-1 min-w-0">
                  <p style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Personal</p>
                  <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>Always saved here first</p>
                </div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 8, backgroundColor: "#F3F4F6", color: "#6B7280", fontSize: 11, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>🔒 Private</span>
              </div>

              {/* Selected group or "Also share" link */}
              {selectedShareGroup ? (
                <div className="flex items-center gap-3 py-2" style={{ borderTop: "1px solid #F3F4F6" }}>
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: (GROUP_TYPE_CONFIG[selectedShareGroup.type] || GROUP_TYPE_CONFIG.custom).bg }}>
                    <span style={{ fontSize: 16 }}>{(GROUP_TYPE_CONFIG[selectedShareGroup.type] || GROUP_TYPE_CONFIG.custom).icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{selectedShareGroup.name}</p>
                    <p style={{ color: "#16A34A", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>Will be shared</p>
                  </div>
                  <button onClick={() => setSelectedShareGroup(null)} className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: "#FEE2E2" }}>
                    <X className="w-3.5 h-3.5" style={{ color: "#EF4444" }} />
                  </button>
                </div>
              ) : shareGroups.length > 0 ? (
                <button onClick={() => setShowGroupPicker(true)} className="flex items-center gap-3 py-2 w-full" style={{ borderTop: "1px solid #F3F4F6" }}>
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: "#EEF2FF" }}>
                    <span style={{ color: PRIMARY, fontSize: 18, fontWeight: 700 }}>+</span>
                  </div>
                  <p style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Also share with a circle</p>
                </button>
              ) : null}
            </div>
          </div>
        )}

        {/* Group picker bottom sheet */}
        {showGroupPicker && (
          <>
            <div onClick={() => setShowGroupPicker(false)} className="fixed inset-0 z-50" style={{ backgroundColor: "rgba(0,0,0,0.4)" }} />
            <div className="fixed left-0 right-0 bottom-0 z-50" style={{ backgroundColor: "#FFFFFF", borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: "12px 20px 32px", boxShadow: "0 -8px 32px rgba(0,0,0,0.15)" }}>
              <div className="flex justify-center mb-4"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: "#D1D5DB" }} /></div>
              <p style={{ color: "#6B7280", fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif", letterSpacing: 1, marginBottom: 16 }}>SHARE WITH A GROUP</p>
              {shareGroups.map((g) => {
                const cfg = GROUP_TYPE_CONFIG[g.type] || GROUP_TYPE_CONFIG.custom;
                return (
                  <button key={g.id} onClick={() => { setSelectedShareGroup(g); setShowGroupPicker(false); }} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: cfg.bg }}>
                      <span style={{ fontSize: 18 }}>{cfg.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{g.name}</p>
                      <p style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>{(GROUP_TYPE_CONFIG[g.type] || GROUP_TYPE_CONFIG.custom).bg ? '' : ''}{g.member_count} member{g.member_count !== 1 ? "s" : ""}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <p style={{ color: "#9CA3AF", fontSize: 11, textAlign: "center", fontFamily: "DM Sans, sans-serif" }}>
          Supports JPEG · PNG · HEIC · PDF
        </p>

        {/* Physical receipt disclaimer */}
        {!isProcessing && (
          <p style={{ color: "#9CA3AF", fontSize: 11, textAlign: "center", fontFamily: "DM Sans, sans-serif", lineHeight: 1.5, marginTop: -4 }}>
            💡 Some shops may ask for the original — please keep your paper receipt.
          </p>
        )}

        <input ref={cameraInputRef} type="file" accept="image/*,.heic,.heif" capture="environment" className="hidden" onChange={handleImageCapture} />
        <input ref={uploadInputRef} type="file" accept="image/*,.heic,.heif,.pdf" className="hidden" onChange={handleImageCapture} />
      </div>

      {/* RIGHT: Form or Processing state */}
      <div className={isMobile ? "mt-4" : ""}>

        {/* ── Processing state ── */}
        {isProcessing ? (
          <div
            className="flex flex-col gap-4 rounded-2xl"
            style={{ backgroundColor: "#FFFFFF", border: "1px solid #F3F4F6", padding: isMobile ? 16 : 20 }}
            data-testid="processing-state"
          >
            <div>
              <p style={{ fontFamily: "Manrope, sans-serif", fontWeight: 700, fontSize: 15, color: "#111827", marginBottom: 4 }}>
                {isLongReceipt ? "Reading your long receipt…" : "Reading your receipt…"}
              </p>
              <p style={{ fontFamily: "DM Sans, sans-serif", fontSize: 12, color: isLongReceipt ? "#D97706" : "#9CA3AF" }}>
                {isLongReceipt
                  ? "This looks like a long receipt — may take up to 90 seconds."
                  : "This takes about 3–5 seconds"}
              </p>
            </div>

            {/* Progress bar */}
            <div style={{ height: 4, backgroundColor: "#F3F4F6", borderRadius: 2, overflow: "hidden" }}>
              <div
                className="progress-pulse"
                style={{ height: 4, borderRadius: 2, background: `linear-gradient(90deg, ${PRIMARY}, ${PRIMARY_DARK})` }}
              />
            </div>

            {/* Steps */}
            <div className="flex flex-col gap-3">
              {[
                { label: "Image received", status: "done" },
                { label: "Scanning details", status: "active" },
                { label: "Ready to review", status: "pending" },
              ].map(({ label, status }) => (
                <div key={label} className="flex items-center gap-3">
                  {status === "done" && (
                    <div style={{ width: 22, height: 22, borderRadius: 999, backgroundColor: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ color: "#16A34A", fontSize: 11, fontWeight: 700 }}>✓</span>
                    </div>
                  )}
                  {status === "active" && (
                    <div style={{ width: 22, height: 22, borderRadius: 999, backgroundColor: PRIMARY, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Loader2 className="w-3 h-3 animate-spin" style={{ color: "#fff" }} />
                    </div>
                  )}
                  {status === "pending" && (
                    <div style={{ width: 22, height: 22, borderRadius: 999, backgroundColor: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ color: "#9CA3AF", fontSize: 10, fontWeight: 600 }}>3</span>
                    </div>
                  )}
                  <span style={{ fontSize: 13, fontFamily: "DM Sans, sans-serif", color: status === "active" ? PRIMARY : status === "done" ? "#16A34A" : "#9CA3AF", fontWeight: status === "active" ? 600 : 400 }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Skeleton form fields */}
            <div className="space-y-3 pt-2">
              {[100, 60, 80].map((w, i) => (
                <div key={i} className="animate-pulse" style={{ height: i === 0 ? 48 : 40, width: `${w}%`, borderRadius: 12, backgroundColor: "#F3F4F6" }} />
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* ── Document type banner ── */}
            {documentType && documentType !== "retail_receipt" ? (() => {
              const banners = {
                bank_transfer:  { Icon: Send,        bg: "#FFFBEB", border: "#FCD34D", iconBg: "#FEF3C7", iconColor: "#D97706", title: "Bank transfer detected",      sub: "We've filled in what we can — check and edit below" },
                online_order:   { Icon: ShoppingBag, bg: "#EFF6FF", border: "#BFDBFE", iconBg: "#DBEAFE", iconColor: "#2D5BFF", title: "Online order detected",        sub: "We've filled in what we can — check and edit below" },
                bank_statement: { Icon: CreditCard,  bg: "#FFFBEB", border: "#FCD34D", iconBg: "#FEF3C7", iconColor: "#D97706", title: "Multiple transactions found",  sub: "All added together — edit the total if you only want to log some" },
                unreadable:     { Icon: AlertCircle, bg: "#F9FAFB", border: "#E5E7EB", iconBg: "#F3F4F6", iconColor: "#6B7280", title: "Couldn't read this image",      sub: "No problem — fill in the details below and save" },
              };
              const b = banners[documentType];
              if (!b) return null;
              return (
                <div className="flex items-start gap-3 mb-4 rounded-xl" style={{ backgroundColor: b.bg, border: `1px solid ${b.border}`, padding: "11px 13px" }} data-testid="doc-type-banner">
                  <div className="flex items-center justify-center flex-shrink-0 rounded-lg" style={{ width: 30, height: 30, backgroundColor: b.iconBg }}>
                    <b.Icon className="w-4 h-4" style={{ color: b.iconColor }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: b.iconColor, marginBottom: 2, fontFamily: "Manrope, sans-serif" }}>{b.title}</p>
                    <p style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.4, fontFamily: "DM Sans, sans-serif" }}>{b.sub}</p>
                  </div>
                </div>
              );
            })() : null}

            {isMobile ? (
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px" style={{ backgroundColor: "#E5E7EB" }} />
                <span style={{ color: "#6B7280", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>Verify information</span>
                <div className="flex-1 h-px" style={{ backgroundColor: "#E5E7EB" }} />
              </div>
            ) : (
              <h3 className="mb-5" style={{ color: "#111827", fontSize: 18, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                Verify information
              </h3>
            )}

            {/* ── Soft warning (partial read — some fields missing) ── */}
            {error ? (
              <div className="mb-4 flex items-start gap-3 rounded-xl" style={{ backgroundColor: "#FEF3C7", border: "1px solid #FDE68A", padding: "12px 14px" }} data-testid="soft-warning">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#D97706" }} />
                <p style={{ color: "#78350F", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>{error}</p>
              </div>
            ) : null}

            {/* ── Form fields ── */}
            <div className="space-y-4 relative">
              {/* Merchant */}
              <div>
                <FieldLabel>{documentType === "bank_transfer" ? "Payee" : "Merchant"}</FieldLabel>
                <ScanInput
                  icon={Store}
                  placeholder={documentType === "bank_transfer" ? "Who did you pay?" : "Enter shop name"}
                  value={formData.merchantName}
                  onChange={(e) => handleInputChange("merchantName", e.target.value)}
                  error={!!validationErrors.merchantName}
                  data-testid="merchant-input"
                  filled={!!formData.merchantName}
                />
                {validationErrors.merchantName ? (
                  <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4, fontFamily: "DM Sans, sans-serif" }}>{validationErrors.merchantName}</p>
                ) : null}
              </div>

              {/* Date + Total */}
              <div className="grid gap-3" style={{ gridTemplateColumns: "3fr 2fr" }}>
                <div>
                  <FieldLabel>Date</FieldLabel>
                  <div
                    className="relative flex items-center w-full rounded-xl"
                    style={{ height: 48, backgroundColor: "#FFFFFF", border: `1px solid ${validationErrors.transactionDate ? "#DC2626" : formData.transactionDate ? "#2D5BFF44" : "#E5E7EB"}`, paddingLeft: 12, paddingRight: 12, gap: 8 }}
                  >
                    <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: validationErrors.transactionDate ? "#DC2626" : "#9CA3AF" }} />
                    <input
                      type="date"
                      value={formData.transactionDate}
                      onChange={(e) => handleInputChange("transactionDate", e.target.value)}
                      className="flex-1 min-w-0 bg-transparent outline-none"
                      style={{ fontSize: 16, fontFamily: "DM Sans, sans-serif", color: "#111827", border: "none", padding: 0 }}
                      data-testid="date-input"
                    />
                  </div>
                  {validationErrors.transactionDate ? (
                    <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4, fontFamily: "DM Sans, sans-serif" }}>{validationErrors.transactionDate}</p>
                  ) : null}
                </div>
                <div>
                  <FieldLabel>Total</FieldLabel>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: validationErrors.totalAmount ? "#DC2626" : "#9CA3AF", fontSize: 15, fontWeight: 600 }}>{symbol}</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.totalAmount}
                      onChange={(e) => handleInputChange("totalAmount", e.target.value)}
                      className="w-full rounded-xl"
                      style={{ height: 48, paddingLeft: 36, paddingRight: 12, backgroundColor: "#FFFFFF", border: `1px solid ${validationErrors.totalAmount ? "#DC2626" : formData.totalAmount ? "#2D5BFF44" : "#E5E7EB"}`, fontSize: 16, fontFamily: "DM Sans, sans-serif", color: "#111827", outline: "none" }}
                      data-testid="amount-input"
                    />
                  </div>
                  {validationErrors.totalAmount ? (
                    <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4, fontFamily: "DM Sans, sans-serif" }}>{validationErrors.totalAmount}</p>
                  ) : null}
                </div>
              </div>

              {/* Category */}
              <div ref={dropdownRef}>
                <FieldLabel>Category</FieldLabel>
                <div className="relative">
                  <ShoppingBag className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#9CA3AF" }} />
                  <button
                    type="button"
                    onClick={() => setShowCategoryDropdown((s) => !s)}
                    className="w-full flex items-center justify-between rounded-xl text-left"
                    style={{ height: 48, paddingLeft: 40, paddingRight: 14, backgroundColor: "#FFFFFF", border: `1px solid ${validationErrors.category ? "#DC2626" : formData.category ? "#2D5BFF44" : "#E5E7EB"}`, fontSize: 15, fontFamily: "DM Sans, sans-serif", color: formData.category ? "#111827" : "#9CA3AF" }}
                    data-testid="category-trigger"
                  >
                    {formData.category || "Select category"}
                    <ChevronDown className="w-4 h-4" style={{ color: "#9CA3AF" }} />
                  </button>
                  {showCategoryDropdown ? (
                    <div className="absolute z-10 w-full mt-1 rounded-xl shadow-lg overflow-hidden" style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB", maxHeight: 220, overflowY: "auto" }}>
                      {CATEGORIES.map((cat) => (
                        <button key={cat} type="button" onClick={() => { handleInputChange("category", cat); setShowCategoryDropdown(false); }} className="w-full px-4 py-2.5 text-left transition-colors hover:bg-blue-50" style={{ backgroundColor: formData.category === cat ? "#EFF6FF" : "transparent", color: "#111827", fontSize: 14, fontFamily: "DM Sans, sans-serif" }}>
                          {cat}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                {validationErrors.category ? (
                  <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4, fontFamily: "DM Sans, sans-serif" }}>{validationErrors.category}</p>
                ) : null}
              </div>

              {/* Notes */}
              <div>
                <FieldLabel optional>Notes</FieldLabel>
                <input
                  type="text"
                  placeholder="Add a note..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  className="w-full rounded-xl px-4"
                  style={{ height: 48, backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB", fontSize: 16, fontFamily: "DM Sans, sans-serif", color: "#111827", outline: "none" }}
                  data-testid="notes-input"
                />
              </div>
            </div>

            {/* Save button — desktop */}
            {!isMobile ? (
              <button
                onClick={handleComplete}
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 rounded-2xl mt-6"
                style={{ background: isSaving ? "#93C5FD" : `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`, color: "#FFFFFF", height: 54, fontSize: 16, fontWeight: 700, fontFamily: "DM Sans, sans-serif", boxShadow: "0 8px 20px rgba(45,91,255,0.25)" }}
                data-testid="save-receipt-btn"
              >
                {isSaving ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving...</> : <><CheckSquare className="w-5 h-5" /> Save Receipt</>}
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* Save button — mobile sticky */}
      {isMobile && !isProcessing ? (
        <button
          onClick={handleComplete}
          disabled={isSaving}
          className="sticky bottom-0 w-full flex items-center justify-center gap-2 mt-6"
          style={{ background: isSaving ? "#93C5FD" : `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`, color: "#FFFFFF", height: 54, fontSize: 16, fontWeight: 700, fontFamily: "DM Sans, sans-serif", boxShadow: "0 8px 20px rgba(45,91,255,0.25)", borderRadius: 18 }}
          data-testid="save-receipt-btn"
        >
          {isSaving ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving...</> : <><CheckSquare className="w-5 h-5" /> Save Receipt</>}
        </button>
      ) : null}

      {/* Cropper — shown immediately after image select, before OCR */}
      {cropperSrc && (
        <CropperOverlay
          imageSrc={cropperSrc}
          onCrop={handleCropConfirm}
          onSkip={handleCropSkip}
          isMobile={isMobile}
        />
      )}

      {/* Buddy error popup — rendered outside the scroll container */}
      <BuddyErrorPopup
        errorType={errorType}
        isUploading={isUploadingPending}
        onSaveAnyway={() => imageFile && uploadAsPending(imageFile)}
        onTryAgain={() => {
          setErrorType(null);
          setImagePreview(null);
          setImageFile(null);
          if (uploadInputRef.current) uploadInputRef.current.value = "";
          if (cameraInputRef.current) cameraInputRef.current.value = "";
          uploadInputRef.current?.click();
        }}
        onEnterManually={() => {
          setErrorType(null);
          // Image stays — user can fill in the form fields manually
        }}
        onClose={() => {
          setErrorType(null);
          navigate("/dashboard", { replace: true });
        }}
      />
    </div>
  );
}

// Re-export ArrowLeft for convenience use in the wrapper modal
export { ArrowLeft };